import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Pencil, Search } from 'lucide-react';
import { AdminDeleteButton } from '../components/AdminDeleteButton';
import { ResetPasswordDialog } from '../components/ResetPasswordDialog';
import { useAuth } from '../contexts/AuthContext';

const ROLES = ['super_admin', 'company_admin', 'site_admin', 'site_user', 'read_only'];
const empty = { name: '', email: '', password: '', mobile: '', role: 'site_user', company_id: '', assigned_sites: [] };

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [data, setData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    Promise.all([api.get('/users'), api.get('/companies'), api.get('/sites')]).then(([u, c, s]) => { setData(u.data); setCompanies(c.data); setSites(s.data); }).finally(() => setLoading(false));
  }, []);
  const fetch = () => api.get('/users').then(r => setData(r.data));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    if (!editing && !form.password) { toast.error('Password is required for new users'); return; }
    try {
      if (editing) { await api.put(`/users/${editing}`, form); toast.success('User updated'); }
      else { await api.post('/users', form); toast.success('User created'); }
      setOpen(false); setForm(empty); setEditing(null); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error saving'); }
  };

  const toggleStatus = async (u) => {
    const s = u.status === 'active' ? 'inactive' : 'active';
    await api.patch(`/users/${u.user_id}/status`, { status: s }); toast.success(`User ${s}`); fetch();
  };

  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success('User deleted'); fetch(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const edit = (u) => { setForm({ name: u.name, email: u.email, password: '', mobile: u.mobile || '', role: u.role, company_id: u.company_id || '', assigned_sites: u.assigned_sites || [] }); setEditing(u.user_id); setOpen(true); };
  const companyName = (id) => companies.find(c => c.company_id === id)?.name || '';
  const filtered = data.filter(u => u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase()));
  const companySites = sites.filter(s => !form.company_id || s.company_id === form.company_id);

  const toggleSite = (siteId) => {
    setForm(f => ({ ...f, assigned_sites: f.assigned_sites.includes(siteId) ? f.assigned_sites.filter(s => s !== siteId) : [...f.assigned_sites, siteId] }));
  };

  return (
    <div data-testid="users-page">
      <div className="page-header">
        <div><h1 className="page-title">Users</h1><p className="text-sm text-slate-500 mt-1">{data.length} total users</p></div>
        <Button onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="add-user-btn"><Plus size={16} className="mr-1.5" />Add User</Button>
      </div>

      <div className="mb-4 relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white border-slate-200" data-testid="search-users" />
      </div>

      <div className="data-table-wrapper">
        <Table>
          <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Company</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow> :
            filtered.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No users found</TableCell></TableRow> :
            filtered.map(u => (
              <TableRow key={u.user_id} className="hover:bg-slate-50/50" data-testid={`user-row-${u.user_id}`}>
                <TableCell className="font-medium text-slate-900">{u.name}</TableCell>
                <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                <TableCell><span className="text-xs font-medium bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full capitalize">{u.role?.replace(/_/g, ' ')}</span></TableCell>
                <TableCell className="text-sm text-slate-600">{companyName(u.company_id)}</TableCell>
                <TableCell><button onClick={() => toggleStatus(u)} className={`status-badge cursor-pointer status-${u.status}`}>{u.status}</button></TableCell>
                <TableCell><div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => edit(u)}><Pencil size={14} /></Button>
                  {currentUser?.role === 'super_admin' && <ResetPasswordDialog email={u.email} name={u.name} testId={`reset-pw-${u.user_id}`} />}
                  <AdminDeleteButton itemName={u.name} description={`Permanently delete user "${u.name}" (${u.email}). This cannot be undone.`} onConfirm={() => remove(u.user_id)} testId={`delete-user-${u.user_id}`} />
                </div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit User' : 'Add User'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="grid grid-cols-2 gap-4 mt-2">
            <div className="form-field"><Label>Full Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-slate-50" data-testid="user-name-input" /></div>
            <div className="form-field"><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-slate-50" data-testid="user-email-input" /></div>
            <div className="form-field"><Label>{editing ? 'New Password' : 'Password *'}</Label><Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="bg-slate-50" placeholder={editing ? 'Leave blank to keep' : ''} data-testid="user-password-input" /></div>
            <div className="form-field"><Label>Mobile</Label><Input value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value})} className="bg-slate-50" /></div>
            <div className="form-field">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                <SelectTrigger className="bg-slate-50" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="form-field">
              <Label>Company</Label>
              <Select value={form.company_id || 'none'} onValueChange={v => setForm({...form, company_id: v === 'none' ? '' : v})}>
                <SelectTrigger className="bg-slate-50" data-testid="user-company-select"><SelectValue placeholder="No company" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No company</SelectItem>{companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {companySites.length > 0 && (
              <div className="form-field col-span-2">
                <Label>Assigned Sites</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1.5 max-h-32 overflow-y-auto">
                  {companySites.map(s => (
                    <label key={s.site_id} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input type="checkbox" checked={form.assigned_sites.includes(s.site_id)} onChange={() => toggleSite(s.site_id)} className="rounded border-slate-300" />
                      <span>{s.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-user-btn">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
